import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, Select } from '~/components/Form';
import { t, validCharacters } from '~/helpers';
import {
  storeCadastroDvec,
  updateCadastroDvec,
} from '~/store/modules/cadastrodvec/actions';
import { closeDialog } from '~/store/modules/dialog/actions';

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const { loading, find, params } = useSelector((state) => state.cadastrodvec);

  const initials = create ? data : { ...find, ...data };

  const status = [
    { label: t('s.geral.opcoes.liberado'), value: 'L' },
    { label: t('s.geral.opcoes.bloqueado'), value: 'B' },
    { label: t('s.geral.opcoes.cancelado'), value: 'C' },
  ];

  const empresaLabel = [
    { label: t('Unidata'), value: '0' },
    { label: t('Cliente'), value: '1' },
  ];

  async function handleSubmit(param) {
    try {
      const schema = Yup.object().shape({
        descricaodispositivoinstalacaodvec: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(30, t('s.geral.validacao.tamanhomaximo').replace('%s', 30))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),

        idcliente:
          create &&
          user.configs.internounidatausuario === 'S' &&
          Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .max(1, t('s.geral.validacao.tamanhomaximo').replace('%s', 1)),

        statusdispositivoinstalacaodvec: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(1, t('s.geral.validacao.tamanhomaximo').replace('%s', 1)),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const objSend = {
        ...initials,
        ...param,
        idcliente: create
          ? param.idcliente === '0'
            ? null
            : user.profile.client
          : initials.idcliente,
        iddispositivoinstalacaodvec: create
          ? null
          : find.iddispositivoinstalacaodvec,
        tokendispositivoinstalacaodvec: create
          ? null
          : find.tokendispositivoinstalacaodvec,
        modelodispositivoinstalacaodvec: create
          ? null
          : find.modelodispositivoinstalacaodvec,
        chavedispositivoinstalacaodvec: create
          ? null
          : find.chavedispositivoinstalacaodvec,
        dataassociacaodispositivoinstalacaodvec: create
          ? null
          : find.dataassociacaodispositivoinstalacaodvec,
      };

      if (create) {
        dispatch(storeCadastroDvec(objSend, params));
        dispatch(dispatch(closeDialog()));
      } else {
        dispatch(updateCadastroDvec(objSend, params));
        dispatch(dispatch(closeDialog()));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (!loading) {
      reference.current.setData(initials);
    }
  }, [loading, initials]);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <Input
            name="descricaodispositivoinstalacaodvec"
            label={t('Nome')}
            variant="outlined"
            fullWidth
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />
          {create && user.configs.internounidatausuario === 'S' && (
            <Select
              name="idcliente"
              label={t('Empresa')}
              options={empresaLabel}
              disabled={!create}
            />
          )}
          {find?.modelodispositivoinstalacaodvec && !create && (
            <Input
              name="modelodispositivoinstalacaodvec"
              label={t('Modelo')}
              variant="outlined"
              fullWidth
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              disabled
            />
          )}
          <Select
            name="statusdispositivoinstalacaodvec"
            label={t('Status')}
            options={status}
          />
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
